import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { getPortalLink } from "../api/user";

const SubscriptionCancelledModal = ({ isOpen, variant = "cancelled" }) => {
  const { user } = useAuth();

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateRemainingDays = () => {
    if (!user?.company?.cancelledInitiatedAt) return 0;
    const initiatedDate = new Date(user.company.cancelledInitiatedAt * 1000);
    const periodEnd = new Date(
      initiatedDate.getTime() + 30 * 24 * 60 * 60 * 1000
    ); // +30 days
    const remainingDays = Math.ceil(
      (periodEnd - new Date()) / (24 * 60 * 60 * 1000)
    );
    return Math.max(0, remainingDays);
  };

  const handleManageBilling = () => {
    getPortalLink().then((res) => {
      window.open(res.url, "_blank");
    });
  };

  const handleCancelCancellation = () => {
    // This would typically call an API to cancel the cancellation
    handleManageBilling();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#00000080] z-50 flex items-center justify-center">
      <div className="max-w-3xl w-full max-h-[90vh] overflow-y-auto bg-white rounded-xl">
        {/* Header */}
        <div className="flex items-center p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <svg
                className="w-6 h-6 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {variant === "cancelling"
                  ? "Cancellation Scheduled"
                  : "Subscription Cancelled"}
              </h2>
              <p className="text-gray-600">
                {variant === "cancelling"
                  ? "Your subscription will be cancelled at the end of your billing period"
                  : "Your subscription has been cancelled"}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Alert Banner */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-orange-600 mb-1">
                  {variant === "cancelling"
                    ? "Cancellation Scheduled"
                    : "Action Required"}
                </p>
                <p className="text-sm text-orange-700">
                  {variant === "cancelling"
                    ? `Your subscription will be cancelled in ${calculateRemainingDays()} days. You can cancel this cancellation anytime.`
                    : "Your subscription has been cancelled. You can reactivate it anytime to continue using all features."}
                </p>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Subscription Details
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Company:</span>
                <span className="font-bold text-gray-900">
                  {user?.company?.name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">
                  Previous Plan:
                </span>
                <span className="font-bold text-gray-900">
                  {user?.company?.planId?.name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">
                  Billing Cycle:
                </span>
                <span className="font-bold text-gray-900">
                  {user?.company?.subscriptionType || "N/A"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">
                  {variant === "cancelling"
                    ? "Cancellation Initiated:"
                    : "Cancellation Date:"}
                </span>
                <span className="font-bold text-orange-600">
                  {variant === "cancelling"
                    ? formatDate(user?.company?.cancelledInitiatedAt)
                    : formatDate(user?.company?.subscriptionCancelledAt)}
                </span>
              </div>
              {variant === "cancelling" && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">
                    Remaining Days:
                  </span>
                  <span className="font-bold text-orange-600">
                    {calculateRemainingDays()} days
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* What Happens Now */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {variant === "cancelling"
                ? "What Happens Next?"
                : "What Happens Now?"}
            </h3>
            <div className="space-y-3">
              {variant === "cancelling" ? (
                <>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700">
                      You can continue using all features until the end of your
                      billing period
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700">
                      You can cancel this cancellation anytime before the period
                      ends
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700">
                      Your subscription will be cancelled on{" "}
                      {formatDate(
                        user?.company?.cancelledInitiatedAt + 30 * 24 * 60 * 60
                      )}
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700">
                      No changes to your current billing cycle
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700">
                      Your account is now on a free plan with limited features
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700">
                      You can reactivate your subscription anytime
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700">
                      Your data and settings are preserved
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700">
                      No additional charges will be made
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Reactivation Benefits - Only show for cancelled subscriptions */}
          {variant === "cancelled" && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Benefits of Reactivating
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700">
                    Full access to all premium features
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700">
                    Unlimited document signing and templates
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700">
                    Priority customer support
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700">
                    Advanced analytics and reporting
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center p-6 border-t border-gray-200 bg-gray-50">
          {variant === "cancelling" ? (
            <button
              onClick={handleCancelCancellation}
              className="bg-primary text-white px-8 py-3 rounded-xl font-bold hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300 transform hover:scale-105"
            >
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span>Cancel Cancellation</span>
              </div>
            </button>
          ) : (
            <button
              onClick={handleManageBilling}
              className="bg-primary text-white px-8 py-3 rounded-xl font-bold hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300 transform hover:scale-105"
            >
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>Reactivate Subscription</span>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCancelledModal;
